import { useState } from 'react';
import GooglePayButton, { ReadyToPayChangeResponse } from '@google-pay/button-react';

import { useAppDispatch } from '@/store/hooks';
import { updateUserDetailsFields } from '@/features/userDetails/userDetailsSlice';
import { separatePaymentDataName } from './googlepayUtils';
import { logger } from '@/services/logger/logger';
import { useJourneyNavigate } from '@/services/journeys/useJourneyNavigate';
import { setComplete, clearErrorMessage, setErrorMessage } from '@/features/paymentProcess/transactionSlice';
import { ErrorCustomiser } from '@/services/error/ErrorContainer';
import SoftHider from '@/components/softHider/SoftHider';
import ButtonLoader from '@/components/loader/ButtonLoader';
import { usePaymentSuccess } from '@/features/paymentProcess/usePaymentSuccess';
import { usePaymentFailure } from '@/features/paymentProcess/usePaymentFailure';

import { useBraintreeGooglepayInit } from './useBraintreeGooglepayInit';
import { useBraintreeCreate } from './useBraintreeCreate';
import { useBraintreePayment } from './useBraintreePayment';
import { useBraintreeExecute } from './useBraintreeExecute';

import GooglePayButtonWrapper from './GooglepayButton.style';

function GooglepayButton({
  disabled = false,
  onClick = () => {},
  setProcessing = () => {},
  testId = 'paymentSelectGooglepay'
}: {
  disabled?: boolean,
  onClick?: () => void,
  setProcessing?: (newProcessing: boolean) => void,
  testId?: string,
} = {}) {
  const dispatch = useAppDispatch();

  const journeyNavigate = useJourneyNavigate();
  const paymentSuccess = usePaymentSuccess();
  const paymentFailure = usePaymentFailure();

  const [ready, setReady] = useState(false);

  const { environment, paymentRequest, braintreeGooglePayment } = useBraintreeGooglepayInit();
  const { triggerCreateApi } = useBraintreeCreate();
  const { triggerPayment } = useBraintreePayment();
  const { triggerExecuteApi } = useBraintreeExecute();

  if (disabled) {
    return null;
  }

  if (!environment || !paymentRequest || !braintreeGooglePayment) {
    return <ButtonLoader />;
  }

  const onReady = (result: ReadyToPayChangeResponse) => {
    setReady(result.isReadyToPay);
  };

  const handleOnClick = () => {
    setProcessing(true);
    onClick();
  };

  const onPayment = (paymentData: google.payments.api.PaymentData) => {
    async function handlePayment() {
      if (!braintreeGooglePayment) {
        logger.error(new Error('Error: BraintreeGooglepay not set'));
        return;
      }

      // Create payment-service transaction
      const transactionId = await triggerCreateApi(
        paymentData,
        {
          onError: () => {
            dispatch(setErrorMessage('Error initiating GooglePay payment'));
            journeyNavigate('error');
          },
        }
      );
      // Note: Passing transactionId by variable (not state) to avoid "stale closures" issue in triggerExecuteApi()
      if (!transactionId) {
        dispatch(setErrorMessage('Error initiating GooglePay payment'));
        journeyNavigate('error');
        return;
      }

      // Parse GooglePayment response on Braintree
      const paymentResponse = await braintreeGooglePayment.parseResponse(paymentData);
      if (!paymentResponse) {
        dispatch(setErrorMessage('Error processing GooglePay payment'));
        paymentFailure();
        return;
      }

      // Trigger Braintree payment (including 3DS handling)
      const paymentResult = await triggerPayment(paymentData, paymentResponse);
      if (!paymentResult) {
        dispatch(setErrorMessage('Error processing GooglePay payment'));
        paymentFailure();
        return;
      }

      // Execute payment-service transaction
      // Note: Using transactionId directly to avoid "stale closures" issue experienced when trying
      // to use transactionId dispatched from earlier function (triggerCreateApi()) in this context
      await triggerExecuteApi(
        paymentData,
        paymentResult.nonce,
        transactionId,
        {
          onSuccess: () => {
            const firstName = separatePaymentDataName(paymentData?.shippingAddress?.name ?? '').firstName;
            if (firstName) {
              dispatch(updateUserDetailsFields({ firstName }));
            }
            dispatch(setComplete(true));
            dispatch(clearErrorMessage());
            paymentSuccess();
          },
          onError: (error) => {
            dispatch(setErrorMessage(ErrorCustomiser(error, 'Error executing GooglePay payment')));
            paymentFailure();
          },
        }
      );
    }
    void handlePayment();
  };

  const onCancel = () => {
    setProcessing(false);
    logger.info('GooglePay cancelled');
  };

  const onError = (error: google.payments.api.PaymentsError | Error) => {
    logger.error('Error on GooglePay');
    logger.error(error);

    setProcessing(false);
    dispatch(setErrorMessage('Error on GooglePay'));
    paymentFailure();
  };

  return (
    <SoftHider show={ready} loader={<ButtonLoader />}>
      <GooglePayButtonWrapper data-test={testId}>
        <GooglePayButton
          environment={environment}
          paymentRequest={paymentRequest}
          onReadyToPayChange={onReady}
          onLoadPaymentData={onPayment}
          onClick={handleOnClick}
          onCancel={onCancel}
          onError={onError}
          buttonSizeMode="fill"
          buttonType="pay"
          buttonColor="black"
          buttonRadius={8}
          style={{
            height: '2.9rem',
          }}
        />
      </GooglePayButtonWrapper>
    </SoftHider>
  );
}

export default GooglepayButton;
